'use client';

import { Mars, NonBinary, Venus, type LucideIcon } from 'lucide-react';

import { cn } from '../lib/utils';
import {
  ButtonToggleSelector,
  type ButtonToggleSelectorOption,
} from './button-toggle-selector';

/**
 * Default symbol icon per known gender value. Icons are kept SECONDARY to the
 * text label (best practice: don't let iconography reduce identity). Override
 * per option via the option's `icon`, or set `hideIcons`.
 */
const ICON_BY_VALUE: Record<string, LucideIcon> = {
  male: Mars,
  female: Venus,
  nonBinary: NonBinary,
};

/**
 * Default option set. Ballee competition categories are binary, so the default
 * is male/female; pass `options` for any other set. Labels are library-default
 * fallbacks — app code should pass translated labels.
 */
export const DEFAULT_GENDER_OPTIONS: ButtonToggleSelectorOption<string>[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

export interface GenderSelectorProps {
  /** Selected value (controlled). */
  value?: string | null;
  /** Called with the newly-selected value, or '' when the selection is cleared. */
  onChange: (value: string) => void;
  /** Segments to render. Defaults to {@link DEFAULT_GENDER_OPTIONS}. */
  options?: ButtonToggleSelectorOption<string>[];
  /** Render labels without the leading symbol icon. */
  hideIcons?: boolean;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  /** Accessible name for the radiogroup when no visible label is associated. */
  'aria-label'?: string;
}

/**
 * Segmented single-select for gender, rendered as equal-width pills with the
 * selected segment filled in the primary colour and a leading symbol icon.
 * A thin wrapper over {@link ButtonToggleSelector} (`mode="single"`, which is a
 * WAI-ARIA radiogroup with keyboard navigation) so there is one segmented
 * single-select implementation, not two.
 */
export function GenderSelector({
  value,
  onChange,
  options = DEFAULT_GENDER_OPTIONS,
  hideIcons = false,
  disabled = false,
  className,
  buttonClassName,
  'aria-label': ariaLabel,
}: GenderSelectorProps) {
  const resolvedOptions = options.map((option) => ({
    ...option,
    icon: hideIcons ? undefined : (option.icon ?? ICON_BY_VALUE[option.value]),
  }));

  return (
    <ButtonToggleSelector
      mode="single"
      value={value ?? undefined}
      // Single mode emits `undefined` when the selected segment is toggled off;
      // surface that as '' so the form field clears rather than going stale.
      onChange={(next) => onChange(typeof next === 'string' ? next : '')}
      options={resolvedOptions}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn('w-full', className)}
      buttonClassName={cn('flex-1', buttonClassName)}
    />
  );
}
